<script setup lang="ts">
import { useStrapiMedia, useTimeAgo } from '#imports'
import { resolveMediaUrl, isLocalMedia } from '@nimbus/utils/resolveMediaUrl'

export type BlogPostProps = {
    title: string
    subtitle: string
    label: string
    mediaUrl: string
    date: string
    tags?: string[]
}

const props = defineProps<BlogPostProps>()
const { getStrapiMediaUrl } = useStrapiMedia()
</script>

<template>
    <div class="blog-post">
        <div class="blog-post__thumbnail">
            <NuxtImg
                v-if="props.mediaUrl"
                :src="resolveMediaUrl(props.mediaUrl, getStrapiMediaUrl)"
                :provider="isLocalMedia(props.mediaUrl) ? 'cloudflare' : undefined"
                :preset="isLocalMedia(props.mediaUrl) ? 'local' : undefined"
                width="80"
                height="80"
                loading="lazy"
                decoding="async"
            />
        </div>

        <div class="blog-post__content">
            <div class="blog-post__date">
                <div class="blog-post__label">
                    {{ props.label }}
                </div>

                <div class="blog-post__created">
                    {{ useTimeAgo(props.date, { once: true }) }}
                </div>
            </div>

            <div class="blog-post__heading">
                <!-- eslint-disable-next-line vue/no-v-html props.title is safe -->
                <div class="blog-post__title" v-html="props.title" />
                <!-- eslint-disable-next-line vue/no-v-html props.subtitle is safe -->
                <div class="blog-post__subtitle" v-html="props.subtitle" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.blog-post {
    display: grid;
    grid-template-columns: 80px 1fr auto;
    gap: 20px;

    border-radius: $border-radius;
    padding: 12px;
    background-color: $panel-color;

    cursor: pointer;

    &:hover img {
        scale: 1.05;
    }

    &__thumbnail {
        height: 100%;
        width: 100%;
        border-radius: $border-radius;
        overflow: hidden;

        > img {
            height: 100%;
            width: 100%;
            object-fit: cover;
            border-radius: $border-radius;
            transition: all 120ms ease-in-out;
        }
    }

    &__date {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__label {
        font-size: 12px;
        font-weight: 600;
        color: $badge-blog-color;
        background-color: $badge-blog-bg;
        border-radius: var(--border-radius, $border-radius);
        line-height: 1;
        height: min-content;
        padding: 4px 6px;
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        color: $body-font-color;
    }

    &__subtitle {
        font-size: 14px;
        color: $body-font-color;
    }

    &__created {
        font-size: 12px;
        font-weight: 700;
        color: $panel-font-color;
    }
}
</style>
